/** Copyright (c) 2020 T. Rowe Price. All rights reserved. */

import './LinkedBankAccounts.scss';

import { isEmpty } from 'lodash';
import { bool } from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { ReactComponent as BankIcon } from 'assets/icons/bank.svg';
import PreferencesHeader from 'components/layouts/Preferences/PreferencesHeader';
import PreferencesRow from 'components/layouts/Preferences/shared/PreferencesRow';
import { linkBuilder, linkedBankAccountsLinks } from 'services/Externals';
import ActionButtonLink from 'components/shared/Buttons/ActionButtonLink';
import DeleteConfirmationModal from 'components/shared/DeleteConfirmationModal/DeleteConfirmationModal';
import {
  getAddAccountsOrderedByTypeName,
  getDeleteAccountsOrderedByTypeName,
  getBankAccountToUpdate,
  showNewBankAccountButton,
  getAddUpdatedAssociations,
  getDeleteUpdatedAssociations
} from 'state/linkedBankAccounts/LinkedBankAccountsSelectors';
import {
  getBankProfile,
  captureBankingBusinessEvent,
  reinitiateChallengeDeposits,
  captureReinitiateBusinessEvent
} from 'services/Rest';
import {
  getCustomer,
  getPrimaryEmailAddress
} from 'state/customer/CustomerSelectors';
import { getView, isSalesforceRepEnabled } from 'state/view/ViewSelectors';

import StopTransferZeroState from './StopTransferZeroState';
import ZeroState from './ZeroState';
import StopTransferBoxInfo from 'components/layouts/Preferences/LinkedBankAccounts/StopTransferBoxInfo';
import FundsBoxInfo from 'components/layouts/Preferences/LinkedBankAccounts/FundsBoxInfo';
import LinkedBankAccountSummary from 'components/layouts/Preferences/LinkedBankAccounts/LinkedBankAccountSummary';
import LinkInvestmentsModal from 'components/layouts/Preferences/LinkedBankAccounts/LinkInvestmentsModal';
import {
  deleteExistingBank,
  buildBusinessEvent,
  buildReinitiateChallengeDepositsBusinessEvent,
  BANK_EVENT_TYPES,
  REINITIATE_EVENT_TYPES,
  prepareBankPayload,
  tokenDisclaimer
} from 'services/LinkedBankAccounts';
import Store from 'state/Store';
import {
  changeBankToUpdate,
  updateAddAssociation,
  clearAddAssociations,
  selectAllAddAssociations,
  updateDeleteAssociation,
  clearDeleteAssociations,
  selectAllDeleteAssociations,
  changeBankModalState
} from 'state/linkedBankAccounts/LinkedBankAccountsActions';
import { analyticsActionSuccess } from 'state/analytics/AnalyticsActions';
import { newDeleteBankEnabled } from 'services/FeatureFlags';

class LinkedBankAccounts extends Component {
  state = {
    showDeleteModal: false,
    showUpdateModal: false,
    bankAccountToDelete: null
  };

  componentDidMount() {
    getBankProfile();
  }

  handleClickUpdate = (bankAccount) => {
    Store.dispatch(changeBankToUpdate(bankAccount));

    this.setState({
      showUpdateModal: true
    });
  };

  handleCloseUpdate = () => {
    Store.dispatch(changeBankToUpdate(null));
    Store.dispatch(changeBankModalState('add'));
    Store.dispatch(clearDeleteAssociations());
    Store.dispatch(clearAddAssociations());
    this.setState({
      showUpdateModal: false
    });
  };

  handleSaveAndClose = () => {
    const {
      customerEmail,
      bankAccountToUpdate,
      customer,
      updatedAddAssociations,
      updatedDeleteAssociations,
      view
    } = this.props;

    if (!isEmpty(updatedAddAssociations)) {
      const event = buildBusinessEvent(
        BANK_EVENT_TYPES.UPDATE,
        customerEmail,
        customer,
        view,
        bankAccountToUpdate,
        updatedAddAssociations
      );
      captureBankingBusinessEvent(event);
    } else if (!isEmpty(updatedDeleteAssociations)) {
      const event = buildBusinessEvent(
        BANK_EVENT_TYPES.UPDATE,
        customerEmail,
        customer,
        view,
        bankAccountToUpdate,
        updatedDeleteAssociations
      );
      captureBankingBusinessEvent(event);
    }

    Store.dispatch(changeBankToUpdate(null));
    Store.dispatch(clearDeleteAssociations());
    Store.dispatch(clearAddAssociations());
    this.setState({
      showUpdateModal: false
    });

    getBankProfile();
  };

  handleDeleteAndClose = () => {
    const { customerEmail, customer, view } = this.props;

    const { bankAccountToDelete } = this.state;

    const event = buildBusinessEvent(
      BANK_EVENT_TYPES.DELETE,
      customerEmail,
      customer,
      view,
      bankAccountToDelete
    );
    captureBankingBusinessEvent(event);

    Store.dispatch(analyticsActionSuccess('ProfPref', 'BankAccountDelete'));

    Store.dispatch(changeBankToUpdate(null));
    Store.dispatch(clearDeleteAssociations());
    Store.dispatch(clearAddAssociations());
    this.setState({
      showDeleteModal: false
    });

    getBankProfile();
  };

  handleClickReinitiateChallengeDeposits = (bankAccount) => {
    const { customerEmail, customer, view } = this.props;

    let payload = prepareBankPayload(bankAccount);
    reinitiateChallengeDeposits({ bankAccount: payload }, () => {
      const event = buildReinitiateChallengeDepositsBusinessEvent(
        REINITIATE_EVENT_TYPES.REINITIATE,
        customerEmail,
        customer,
        view,
        bankAccount
      );
      captureReinitiateBusinessEvent(event);
    });
  };

  handleClickDelete = (bankAccount) => {
    this.setState({
      showDeleteModal: true,
      bankAccountToDelete: bankAccount
    });
  };

  handleCloseDelete = () => {
    this.setState({
      showDeleteModal: false
    });
  };

  handleUpdateAddAssociation = (account) => {
    Store.dispatch(updateAddAssociation(account));
  };

  handleSelectAllAdd = () => {
    const { selectAllAddAccounts } = this.props;
    Store.dispatch(selectAllAddAssociations(selectAllAddAccounts));
  };

  handleDeselectAllAdd = () => {
    Store.dispatch(clearAddAssociations());
  };

  handleUpdateDeleteAssociation = (account) => {
    Store.dispatch(updateDeleteAssociation(account));
  };

  handleSelectAllDelete = () => {
    const { selectAllDeleteAccounts } = this.props;
    Store.dispatch(selectAllDeleteAssociations(selectAllDeleteAccounts));
  };

  handleDeselectAllDelete = () => {
    Store.dispatch(clearDeleteAssociations());
  };

  handleSubmitDelete = () => {
    const { bankAccountToDelete } = this.state;
    const isDeleteCompleteBank = newDeleteBankEnabled();
    deleteExistingBank(bankAccountToDelete, this.handleDeleteAndClose, isDeleteCompleteBank);
  };

  render() {
    const { isSalesforceRepEnabled, showNewBankAccountButton, view } =
      this.props;
    const { showDeleteModal, showUpdateModal } = this.state;

    const deletePrompt = 'Are you sure you want to delete this bank account?';

    return (
      <div className="linked-bank-accounts-container">
        {!isSalesforceRepEnabled && (
          <PreferencesHeader>
            <div className="linked-bank-accounts-header">
              Bank Accounts
            </div>
          </PreferencesHeader>
        )}

        <PreferencesRow className="linked-bank-accounts">
          <main className="card-section">
            {showNewBankAccountButton && !isSalesforceRepEnabled && (
              <ActionButtonLink
                className="link-new-bank-account-button"
                href={linkBuilder(linkedBankAccountsLinks.addNewBank, view)}
                id="link-new-bank-account-button"
                type="secondary">
                <BankIcon className="bank-button-responsive" />
                Link New Bank Account
              </ActionButtonLink>
            )}

            <ZeroState />

            <StopTransferZeroState />

            <LinkedBankAccountSummary
              onClickReinitiateChallengeDeposits={
                this.handleClickReinitiateChallengeDeposits
              }
              onClickDelete={this.handleClickDelete}
              onClickUpdate={this.handleClickUpdate}
            />

            <DeleteConfirmationModal
              deletePrompt={deletePrompt}
              onCancel={this.handleCloseDelete}
              onSubmit={this.handleSubmitDelete}
              requestStatusKey="deleteBank"
              showModal={showDeleteModal}
              shouldDisable
            />
            {showUpdateModal && (
              <LinkInvestmentsModal
                showModal={showUpdateModal}
                handleCloseUpdate={this.handleCloseUpdate}
                onUpdateAddAssociation={this.handleUpdateAddAssociation}
                handleSaveAndClose={this.handleSaveAndClose}
                handleSelectAllAdd={this.handleSelectAllAdd}
                handleDeselectAllAdd={this.handleDeselectAllAdd}
                onUpdateDeleteAssociation={this.handleUpdateDeleteAssociation}
                handleSelectAllDelete={this.handleSelectAllDelete}
                handleDeselectAllDelete={this.handleDeselectAllDelete}
              />
            )}

            <StopTransferBoxInfo />

            {!isSalesforceRepEnabled && <FundsBoxInfo />}
            <div>{tokenDisclaimer}</div>
          </main>
          <aside>
            {showNewBankAccountButton && !isSalesforceRepEnabled && (
              <ActionButtonLink
                className="link-new-bank-account-button"
                href={linkedBankAccountsLinks.addNewBank}
                id="link-new-bank-account-button"
                type="secondary">
                <BankIcon />
                Link New Bank Account
              </ActionButtonLink>
            )}

            <StopTransferBoxInfo />

            {!isSalesforceRepEnabled && <FundsBoxInfo />}
          </aside>
        </PreferencesRow>
      </div>
    );
  }
}

LinkedBankAccounts.propTypes = {
  showNewBankAccountButton: bool
};

LinkedBankAccounts.defaultProps = {
  showNewBankAccountButton: false
};

const mapStateToProps = (state) => ({
  isSalesforceRepEnabled: isSalesforceRepEnabled(state),
  showNewBankAccountButton: showNewBankAccountButton(state),
  selectAllAddAccounts: getAddAccountsOrderedByTypeName(state),
  selectAllDeleteAccounts: getDeleteAccountsOrderedByTypeName(state),
  customerEmail: getPrimaryEmailAddress(state).address || '',
  bankAccountToUpdate: getBankAccountToUpdate(state),
  updatedAddAssociations: getAddUpdatedAssociations(state),
  updatedDeleteAssociations: getDeleteUpdatedAssociations(state),
  customer: getCustomer(state),
  view: getView(state)
});

export default connect(mapStateToProps)(LinkedBankAccounts);
